import { Award, ThumbsDown, Equal, User, AlertCircle } from 'lucide-react';
import { round } from '@/utils/mathUtils';

type Props = {
  minDiscount: number;
  maxDiscount: number;
  competitorsCount: number;
  discountStatus: 'accept' | 'bad' | 'best' | 'only' | 'static';
};

function DiscountToolTipContent({
  competitorsCount,
  maxDiscount,
  minDiscount,
  discountStatus,
}: Props) {
  const areDiscountsEqual = minDiscount === maxDiscount;
  let status = '';
  let desc = '';
  let icon = null;
  let statusColor = '';

  switch (discountStatus) {
    case 'accept':
      status = 'الخصم مقبول';
      desc = 'خصم المورد ليس افضل خصم ولا اقل خصم معروض';
      icon = <AlertCircle className="h-4 w-4" />;
      statusColor = 'text-gray-700 dark:text-gray-50';
      break;
    case 'bad':
      status = 'الخصم سيء';
      desc = 'خصم المورد اقل خصم معروض';
      icon = <ThumbsDown className="h-4 w-4" />;
      statusColor = 'text-red-500 dark:text-red-50';
      break;
    case 'best':
      status = 'افضل خصم';
      desc = 'خصم المورد افضل خصم معروض';
      icon = <Award className="h-4 w-4" />;
      statusColor = 'text-green-700 dark:text-green-50';
      break;
    case 'only':
      status = 'المورد الوحيد الحالي';
      desc = 'المورد الوحيد الذي قدم عرض';
      icon = <User className="h-4 w-4" />;
      statusColor = 'text-indigo-700 dark:text-indigo-50';
      break;
    case 'static':
      status = 'خصم متساوي';
      desc = 'قدم جميع الموردين خصم متساوي';
      icon = <Equal className="h-4 w-4" />;
      statusColor = 'text-fuchsia-700 dark:text-fuchsia-50';
      break;
    default:
      status = '';
      desc = '';
      icon = null;
      statusColor = '';
  }

  return (
    <div className="flex flex-col gap-2 p-2">
      {/* Status Header */}
      <div className="flex flex-col items-center justify-center overflow-hidden rounded-md border border-primary/20 bg-gradient-to-br from-background via-background/95 to-background p-2 shadow-sm dark:border-primary/30">
        <div className="flex items-center gap-2">
          <div className={`rounded-full bg-primary/10 p-1.5 ${statusColor}`}>
            {icon}
          </div>
          <div className={`text-center font-medium ${statusColor}`}>
            {status}
          </div>
        </div>
        <div className="mt-1 text-center text-xs text-muted-foreground">
          {desc}
        </div>
      </div>

      {/* Discount Information */}
      <div className="space-y-2">
        {areDiscountsEqual ? (
          <div className="flex items-center overflow-hidden rounded-md border border-green-300/30 bg-gradient-to-br from-green-100/50 to-green-200/50 p-1.5 shadow-sm dark:border-green-400/30 dark:from-green-400/10 dark:to-green-500/10">
            <div className="flex w-[120px] items-center justify-center rounded bg-gradient-to-r from-green-500 to-green-600 px-2 py-1 text-xs font-medium text-white">
              خصم موحد
            </div>
            <div className="mx-1 flex-1 rounded bg-white/50 px-2 py-1 text-center text-sm font-medium text-green-700 dark:bg-white/10 dark:text-green-50">
              {round(maxDiscount || 0, 2)}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center overflow-hidden rounded-md border border-green-300/30 bg-gradient-to-br from-green-100/50 to-green-200/50 p-1.5 shadow-sm dark:border-green-400/30 dark:from-green-400/10 dark:to-green-500/10">
              <div className="flex w-[120px] items-center justify-center rounded bg-gradient-to-r from-green-500 to-green-600 px-2 py-1 text-xs font-medium text-white">
                افضل خصم
              </div>
              <div className="mx-1 flex-1 rounded bg-white/50 px-2 py-1 text-center text-sm font-medium text-green-700 dark:bg-white/10 dark:text-green-50">
                {round(maxDiscount || 0, 2)}
              </div>
            </div>
            <div className="flex items-center overflow-hidden rounded-md border border-red-300/30 bg-gradient-to-br from-red-100/50 to-red-200/50 p-1.5 shadow-sm dark:border-red-400/30 dark:from-red-400/10 dark:to-red-500/10">
              <div className="flex w-[120px] items-center justify-center rounded bg-gradient-to-r from-red-500 to-red-600 px-2 py-1 text-xs font-medium text-white">
                اسوا خصم
              </div>
              <div className="mx-1 flex-1 rounded bg-white/50 px-2 py-1 text-center text-sm font-medium text-red-700 dark:bg-white/10 dark:text-red-50">
                {round(minDiscount || 0, 2)}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Competitors Count */}
      <div className="flex items-center justify-center overflow-hidden rounded-md border border-primary/20 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 p-1.5 text-sm font-medium text-primary shadow-sm dark:border-primary/30 dark:from-primary/10 dark:via-primary/20 dark:to-primary/10">
        عدد العروض : {competitorsCount || '-'}
      </div>
    </div>
  );
}

export default DiscountToolTipContent;
